import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, IconSpinner, StrapiContentRenderer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import FooterContainer from '../FooterContainer/FooterContainer';
import {
  fetchAllBlogs,
  fetchSingleBlog,
  getBlogSelector,
  getBlogsInProgress,
  getBlogsSelector,
} from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';

import css from './SingleBlogPage.module.css';
import { useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import { parse } from '../../util/urlHelpers';
import Slider from 'react-slick';
import '../../styles/slick/slick.css';
import '../../styles/slick/slick-theme.css';
import SingleBlogCards from './SingleBlogCards/SingleBlogCards';

const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

export const SingleBlogPageComponent = props => {
  const { intl, scrollingDisabled, params, viewport } = props;

  const isMobileLayout = viewport && viewport.width > 0 && viewport.width < 768;
  const config = useConfiguration();
  const dispatch = useDispatch();
  const location = useLocation();

  const search = parse(location.search);

  useEffect(() => {
    dispatch(fetchAllBlogs({ tag: search?.tag }));
  }, [search?.tag]);

  const blogsData = useSelector(getBlogsSelector) || [];

  const searchInProgress = useSelector(getBlogsInProgress);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [sortBy, setSortBy] = useState('recent'); // Default sorting by most recent

  // Effect to filter and sort blogs based on search keyword and sorting option
  useEffect(() => {
    let processedBlogs = [...blogsData];

    // Filter by search keyword
    if (searchKeyword) {
      processedBlogs = processedBlogs.filter(blog => {
        const title = blog.title.toLowerCase();
        const keyword = searchKeyword.toLowerCase();
        return title.includes(keyword);
      });
    }

    // Sort blogs
    if (sortBy === 'recent') {
      processedBlogs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === 'relevance') {
      // Example: Sort by a hypothetical 'relevanceScore' property
      processedBlogs.sort((a, b) => b.relevanceScore - a.relevanceScore);
    }

    setFilteredBlogs(processedBlogs);
  }, [searchKeyword, sortBy, blogsData]); // Dependencies

  const blogs = isArrayLength(filteredBlogs) ? filteredBlogs : blogsData;

  const slug = params?.title;

  useEffect(() => {
    dispatch(fetchSingleBlog({ slug }));
  }, [slug]);

  const blog = useSelector(getBlogSelector);
  const blogsInProgress = useSelector(getBlogsInProgress);

  if (!blog) {
    return (
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.iconSpinner}>
          <IconSpinner />
        </div>
      </LayoutSingleColumn>
    );
  }

  const title = blog?.title || '';
  const description = blog?.description || '';
  const sliderSettings = {
    dots: false,
    arrows: true,
    infinite: true,
    autoplay: false,
    autoplaySpeed: 2000,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    variableWidth: false,
    focusOnSelect: false,
    centerMode: false,
    responsive: [
      {
        breakpoint: 1128,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 2,
          centerMode: true,
        },
      },
      {
        breakpoint: 580,
        settings: {
          slidesToShow: 1,
          centerMode: true,
        },
      },
    ],
  };
  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={description}
      title={title}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: description,
        name: title,
        image: [facebookImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <div className={css.blogsContainer}>
            {blogsInProgress ? (
              <div className={css.iconSpinner}>
                <IconSpinner />
              </div>
            ) : (
              <div className={css.blogsContent}>
                <h1>{title}</h1>
                {description ? <StrapiContentRenderer content={description} /> : null}
                <div className={css.relatedBlogSection}>
                  <h1>{intl.formatMessage({ id: 'BlogPage.similarArticles' })}</h1>
                  <Slider {...{ ...sliderSettings, arrows: !isMobileLayout }}>
                    {searchInProgress ? (
                      <IconSpinner />
                    ) : isArrayLength(blogs) ? (
                      blogs.map((blog, index) => <SingleBlogCards key={index} blog={blog} />)
                    ) : (
                      <p>{intl.formatMessage({ id: 'BlogPage.noBlogPostsFound' })}</p>
                    )}
                  </Slider>
                </div>
              </div>
            )}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

SingleBlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const SingleBlogPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl
)(SingleBlogPageComponent);

export default SingleBlogPage;
